import {OperationType, template as tpl, viewModel as ObjectSearch} from "../object/search";
import {IKnockoutComponent, IObjectData, ITableColumn, TSyncfield} from "../../index";
import * as ko from "knockout";
import {canManageOthersPassword, getRawSyncFieldValue, getSyncFieldValue} from "../../util/misc";
import {_} from 'lodash';
import {global} from "../../appContext";
import {getHash, setRouteHash} from "../../constants/RouteDefinitions";
import {getTabHash} from "../../util/pageShellRoute";
import {IDropdownButton} from "../button";
import search_rowHtml from './search-row.html?raw';

export let template: string = tpl.replace(new RegExp(/<!-- {{TEMPLATE_ROW}} -->/, 'g'), search_rowHtml);

/** Hidden syncfield used to detect deactivated rows in search tuples. */
const HIDDEN_DELETED_SYNCFIELD: TSyncfield = 'Deleted';

export class viewModel extends ObjectSearch {
    private canmanagepassword: boolean = canManageOthersPassword()

    protected override getTableKeys(): string[] {
        return super.getTableKeys().concat(['func']);
    }

    protected override getSearchColsArr(): string[] {
        return _.uniq([...super.getSearchColsArr(), HIDDEN_DELETED_SYNCFIELD]);
    }

    protected override getTableDefinition(): ITableColumn[] {
        const ret = super.getTableDefinition()
        const colIdentityType = ret.find(o => o.key == 'IdentityType');
        if (colIdentityType) {
            colIdentityType.sort = true
            colIdentityType.getCellCls = o => getRawSyncFieldValue('IdentityType', o.syncfields)?.toLowerCase() ?? ''
            colIdentityType.getContent = o => {
                this.data()
                const identityType = getRawSyncFieldValue('IdentityType', o.syncfields);
                if (!identityType) {
                    return '';
                }
                return `<span class="tag">${this.markText(colIdentityType.key, identityType)}</span>`
            }
        }
        return ret
    }

    /** Dim deactivated rows only in mixed/active filters — never when filtered to deactivated only. */
    private shouldDimDeactivatedRow(obj: IObjectData): boolean {
        // -1 = deactivated-only filter ("Vis deaktiverte"); keep full opacity for readability.
        if (Number(this.filterOptions.deleted()) === -1) {
            return false;
        }
        return !!getRawSyncFieldValue(HIDDEN_DELETED_SYNCFIELD, obj.syncfields);
    }

    protected override getRowBindings(obj: IObjectData): Record<string, unknown> {
        return _.defaultsDeep({
            css: {'user-row--deactivated': this.shouldDimDeactivatedRow(obj)},
        }, super.getRowBindings(obj));
    }

    /*******************************************************************************************************************
     * View interaction
     *******************************************************************************************************************/

    onResetPasswordClick(item: IObjectData, col: ITableColumn, e: Event) {
        e.stopImmediatePropagation();
        setRouteHash('resetpassword', {id: item.id});
    }

    onEmulateClick(item: IObjectData, col: ITableColumn, e: Event) {
        e.stopImmediatePropagation();
        this.isRequesting(true);
        global.loginManager.doEmulation(item);
    }

    /*******************************************************************************************************************
     * View helpers
     *******************************************************************************************************************/

    override selectedObjectsSorted = ko.pureComputed((): IObjectData[] => {
        const ret: IObjectData[] = _.sortBy(this.selectedObjects(), (item: IObjectData): string => {
            switch (this.searchcolumn()) {
                case "id":
                case "username":
                    return item[this.searchcolumn() as keyof IObjectData] as string;
                default:
                    return getSyncFieldValue(this.searchcolumn() as TSyncfield, item.syncfields);
            }
        });

        return this.searchascending() ? ret : ret.reverse();
    });

    protected override getRouteHashForItem(type: 'create' | 'read' | 'edit' | 'userpermissions' | 'resetpassword' | 'wizardedituser', obj: any): string {
        switch (type) {
            case 'userpermissions':
                // Shell tab — no own hash; getHash would return null.
                return getTabHash(type, obj) ?? '';
            case 'resetpassword':
            case 'wizardedituser':
                return getHash(type, obj) ?? '';
            default:
                return super.getRouteHashForItem(type, obj) ?? '';
        }
    }

    /*******************************************************************************************************************
     * Client/Server stuff
     *******************************************************************************************************************/

    protected override getSearchCols(): string {
        // Add 'DisplayName' to the searchcols so that a search on full name is possible.
        const ret = this.table()
            .filter((item: ITableColumn) => item.sort)
            .map((item: ITableColumn) => item.key);

        const isGn = this.table().find((item: ITableColumn) => item.key == 'GivenName');
        const isSn = this.table().find((item: ITableColumn) => item.key == 'Surname');
        const isDn = this.table().find((item: ITableColumn) => item.key == 'DisplayName');

        if (isGn && isSn && !isDn) {
            ret.push('DisplayName');
        }

        return ret.join(',');
    }

    private getRowEllipsisDropdownComponent(obj: IObjectData): IKnockoutComponent {
        let buttons: IDropdownButton[];
        if (this.isManual) {
            buttons = [
                {
                    href: () => this.getRouteHashForItem('edit', obj),
                    label: 'edit',
                },
                {
                    href: () => this.getRouteHashForItem('wizardedituser', obj),
                    label: `wizard for editing|subject:((manual|ac)) ((${this.__objDef?.name ?? ''}|ac))`,
                },
                {
                    cls: 'btn-danger btn-ready-calm',
                    label: 'delete',
                    validator: () => this.isOperationAvailable(OperationType.Delete),
                    onClick: () => {
                        this.selectedObjects([obj]);
                        this.onDeleteObjectsClick();
                    },
                },
            ];
        } else {
            buttons = [
                {
                    href: () => this.getRouteHashForItem('resetpassword', obj),
                    label: 'edit password',
                    validator: () => this.canmanagepassword,
                },
                {
                    href: () => this.getRouteHashForItem('userpermissions', obj),
                    label: 'edit permissions',
                    validator: () => this.pv.isMinSuperEmployee(),
                },
                {
                    onClick: () => {
                        this.isRequesting(true);
                        global.loginManager.doEmulation(obj);
                    },
                    label: 'emulate user',
                    validator: () => this.pv.isMinCompanyAdmin(),
                }
            ]
        }
        return {
            name: 'component/button/ellipsis-dropdown',
            params: {buttons: buttons}
        }
    }

}
